import { CONVEYANCE_TYPES } from '@koala/sdk/v4';
import * as Dialog from '@radix-ui/react-dialog';
import type { PointerDownOutsideEvent, FocusOutsideEvent } from '@radix-ui/react-dismissable-layer';
import debounce from 'lodash/debounce';
import { readableColor, transparentize } from 'polished';
import { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { LocationDetails } from './location-details';
import { TimePicker } from './time-picker';
import { DeliveryDetails } from './delivery-details';
import { PickupIcon } from '@/components/uielements/icon/pickup';
import { DeliveryIcon } from '@/components/uielements/icon/delivery';
import { type ConveyanceMode } from '@/redux/conveyanceMode/actions';
import { Stack } from '@/components/ui';
import { API_CONVEYANCE_TYPES } from '@/constants/events';
import { useLocation } from '@/features/locations/service';
import { useDispatch, useSelector } from '@/redux';
import { useBasketMutating } from '@/hooks/useBasketMutating';
import conveyanceModeActions from '@/redux/conveyanceMode/actions';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import { isConveyanceInFlight } from '@/redux/orderStatus/reducer';
import Icon from '@/components/uielements/icon';
import { Spinner } from '@/components/uielements/spinner';
import { safePolished } from '@/utils/safePolished';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import { Render } from '@/components/uielements/render';
import { isUrlServiceTypeDelivery } from '@/utils/urls';
import {
  getFulfillmentTime,
  getReadableRange,
  isAsapAvailable,
  isAsapValue,
} from '@/utils/conveyance';
import { getLeadTimes } from '@/utils/locations';
import { ExclamationCircle } from '@/components/uielements/icon/exclamation-circle';

export function Conveyance() {
  const dispatch = useDispatch();
  const [popoverVisible, setPopoverVisibility] = useState(false);
  // location === location ID
  const { address, time_wanted, location, type } = useSelector(selectConveyance);
  const { conveyanceMode } = useSelector((state) => state.app);
  const conveyanceInFlight = useSelector(isConveyanceInFlight);
  const basketMutating = useBasketMutating();
  const conveyanceDisabled = conveyanceInFlight || basketMutating;
  const { data: storeLocation } = useLocation(location);
  const { cartMigration } = useSelector((state) => state.app.global);
  const [switchedFromDelivery, setSwitchedFromDelivery] = useState(false);
  const deliveryOnly = !storeLocation?.data?.supports_pick_up;
  const leadMinutes = getLeadTimes(storeLocation?.data, conveyanceMode.type);
  const asapEnabled = isAsapAvailable(storeLocation?.data, conveyanceMode.type);
  const supportsScheduledOrders = Boolean(storeLocation?.data?.order_ahead);
  const requiresDeliveryAddress = Boolean(
    type === CONVEYANCE_TYPES.DELIVERY &&
      storeLocation?.data?.supports_delivery &&
      !storeLocation?.data?.supports_pick_up,
  );

  const getConveyanceTimeText = () => {
    if (isAsapValue(time_wanted) && asapEnabled) {
      return `ASAP (${getReadableRange(leadMinutes)})`;
    }
    if (!time_wanted && supportsScheduledOrders) {
      return 'SCHEDULED';
    }
    return getFulfillmentTime(time_wanted, storeLocation?.data);
  };

  const defaultConveyanceDetails: ConveyanceMode = useMemo(
    () => ({
      type: API_CONVEYANCE_TYPES.PICKUP,
      details: [],
    }),
    [],
  );
  const deliveryConveyanceDetails: ConveyanceMode = useMemo(
    () =>
      ({
        type: API_CONVEYANCE_TYPES.DELIVERY,
        details: address ?? {},
      }) as ConveyanceMode,
    [address],
  );

  // Update conveyance mode, main state update function
  const updateConveyance = (
    event: React.MouseEvent<HTMLButtonElement>,
    conveyanceType: CONVEYANCE_TYPES,
  ) => {
    event.stopPropagation();
    const conveyanceDetails: ConveyanceMode =
      conveyanceType === CONVEYANCE_TYPES.PICKUP
        ? defaultConveyanceDetails
        : deliveryConveyanceDetails;
    dispatch(conveyanceModeActions.conveyanceModeSet(conveyanceDetails, time_wanted));
    if (conveyanceType === CONVEYANCE_TYPES.DELIVERY) {
      setSwitchedFromDelivery(false);
    }
  };

  const switchToPickup = () => {
    if (storeLocation?.data?.supports_pick_up) {
      dispatch(conveyanceModeActions.conveyanceModeSet(defaultConveyanceDetails, time_wanted));
      setSwitchedFromDelivery(true);
      setTimeout(() => {
        setSwitchedFromDelivery(false);
      }, 10000);
    }
  };

  useEffect(() => {
    setSwitchedFromDelivery(false);
  }, [popoverVisible]);

  const disableModalClose = (
    event: KeyboardEvent | PointerDownOutsideEvent | FocusOutsideEvent,
  ) => {
    if (requiresDeliveryAddress) {
      event.preventDefault();
    }
  };

  const closePopover = () => {
    setPopoverVisibility(false);

    if (isUrlServiceTypeDelivery()) {
      const url = new URL(window.location.href);
      url.searchParams.delete('service_type');
      window.history.replaceState(window.history.state, '', url);
    }
  };

  // Reset conveyance to pickup if popover is closed without selecting an address for delivery.
  // Bail while a conveyance update is in flight so this reset cannot race the delivery update
  // dispatched from the address-add flow (TEAM-14698).
  //
  // Bail on any other basket write too (TEAM-14696). Unlike a diner's explicit
  // choice, this is a corrective reset — replaying it after the write settles
  // would act on a condition that may no longer hold. The effect re-runs when
  // `basketMutating` clears and re-evaluates against fresh state instead.
  useEffect(() => {
    if (
      !conveyanceInFlight &&
      !basketMutating &&
      !popoverVisible &&
      !address?.street_address &&
      conveyanceMode.type !== CONVEYANCE_TYPES.PICKUP &&
      storeLocation?.data?.supports_pick_up
    ) {
      dispatch(conveyanceModeActions.conveyanceModeSet(defaultConveyanceDetails, time_wanted));
    }
  }, [
    popoverVisible,
    address,
    conveyanceMode.type,
    time_wanted,
    dispatch,
    defaultConveyanceDetails,
    storeLocation?.data?.supports_pick_up,
    conveyanceInFlight,
    basketMutating,
  ]);

  const debouncedSetConveyanceMode = useMemo(
    () =>
      debounce((conveyanceDetails: ConveyanceMode, timeWanted: string | undefined) => {
        dispatch(conveyanceModeActions.conveyanceModeSet(conveyanceDetails, timeWanted));
      }, 400),
    [dispatch],
  );
  // Set conveyance for delivery if URL param is set,
  useEffect(() => {
    if (cartMigration.type !== null) return;
    if (
      (isUrlServiceTypeDelivery() &&
        storeLocation?.data?.supports_delivery &&
        conveyanceMode.type !== CONVEYANCE_TYPES.DELIVERY) ||
      (!storeLocation?.data?.supports_pick_up &&
        storeLocation?.data?.supports_delivery &&
        !address?.street_address)
    ) {
      // Allow popover to open before setting conveyance
      setPopoverVisibility(true);
      // Skip dispatch while a conveyance update is in flight; the effect will re-run and
      // fire the debounced set once the in-flight update settles (TEAM-14698).
      if (!conveyanceInFlight) {
        debouncedSetConveyanceMode(deliveryConveyanceDetails, time_wanted);
      }
    }
    // Keep conveyance closed if we already have an address in local storage
    if (
      !storeLocation?.data?.supports_pick_up &&
      storeLocation?.data?.supports_delivery &&
      address?.street_address
    ) {
      setPopoverVisibility(false);
    }
    return () => {
      debouncedSetConveyanceMode.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeLocation?.data?.supports_delivery,
    storeLocation?.data?.supports_pick_up,
    time_wanted,
    dispatch,
    conveyanceMode.type,
    debouncedSetConveyanceMode,
    address?.street_address,
    conveyanceInFlight,
  ]);

  return (
    <Dialog.Root
      open={popoverVisible}
      onOpenChange={(open: boolean) => {
        open ? setPopoverVisibility(true) : closePopover();
      }}
      modal={true}
    >
      <Trigger asChild>
        <div aria-controls={undefined}>
          <Stack direction="horizontal" gap="0">
            <ButtonsWrapper $isLoading={conveyanceInFlight}>
              <Render condition={!!storeLocation && storeLocation?.data?.supports_delivery}>
                <StyledButton
                  id="delivery-conveyance-button"
                  $active={conveyanceMode.type === CONVEYANCE_TYPES.DELIVERY}
                  $removeLeftBorder={false}
                  $removeRightBorder={storeLocation?.data?.supports_pick_up}
                  aria-expanded={
                    popoverVisible && conveyanceMode.type === CONVEYANCE_TYPES.DELIVERY
                  }
                  aria-haspopup="dialog"
                  aria-label="Select delivery options"
                  disabled={conveyanceDisabled}
                  aria-disabled={conveyanceDisabled || undefined}
                  onClick={(event) => {
                    updateConveyance(event, CONVEYANCE_TYPES.DELIVERY);
                    setPopoverVisibility(true);
                  }}
                >
                  <DeliveryIcon />
                  Delivery
                </StyledButton>
              </Render>
              <Render condition={!!storeLocation && storeLocation?.data?.supports_pick_up}>
                <StyledButton
                  id="pickup-conveyance-button"
                  $active={conveyanceMode.type === CONVEYANCE_TYPES.PICKUP}
                  $removeLeftBorder={storeLocation?.data?.supports_delivery}
                  $removeRightBorder={false}
                  aria-expanded={popoverVisible && conveyanceMode.type === CONVEYANCE_TYPES.PICKUP}
                  aria-haspopup="dialog"
                  aria-label="Select pickup options"
                  disabled={conveyanceDisabled}
                  aria-disabled={conveyanceDisabled || undefined}
                  onClick={(event) => {
                    updateConveyance(event, CONVEYANCE_TYPES.PICKUP);
                    setPopoverVisibility(true);
                  }}
                >
                  <PickupIcon width="20" height="20" />
                  Pickup
                </StyledButton>
              </Render>
              {conveyanceInFlight && (
                <SpinnerOverlay role="status" aria-label="Updating conveyance">
                  <Spinner style={{ height: 20, width: 20, position: 'static' }} />
                </SpinnerOverlay>
              )}
            </ButtonsWrapper>

            <FulfillmentTime>{getConveyanceTimeText()}</FulfillmentTime>
          </Stack>
        </div>
      </Trigger>

      <Dialog.Portal>
        <Container>
          <Overlay $shouldDisplay={popoverVisible} />
          <Content
            $shouldDisplay={popoverVisible}
            aria-label="Delivery and pickup options"
            onEscapeKeyDown={disableModalClose}
            onPointerDownOutside={disableModalClose}
            onInteractOutside={disableModalClose}
          >
            <DetailsSection>
              <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
                <StyledCloseButton asChild aria-label="Close options panel">
                  <Render condition={!requiresDeliveryAddress}>
                    <Icon.Close title="close icon" onClick={closePopover} />
                  </Render>
                </StyledCloseButton>
              </div>
              <div style={{ width: '100%', overflowY: 'auto' }}>
                <Render
                  condition={conveyanceMode.type === CONVEYANCE_TYPES.DELIVERY}
                  whenTrue={
                    <DeliveryDetails
                      onSwitchToPickup={
                        storeLocation?.data?.supports_pick_up ? switchToPickup : undefined
                      }
                    />
                  }
                  whenFalse={
                    <>
                      <Render condition={switchedFromDelivery}>
                        <SwitchedToPickupNote>
                          <ExclamationCircle title="Switched to pickup" />
                          Switched to pickup. No address needed &mdash; select your time below.
                        </SwitchedToPickupNote>
                      </Render>
                      <LocationDetails />
                    </>
                  }
                />
              </div>
            </DetailsSection>
            <Render condition={deliveryOnly}>
              <Divider />
              <Section>
                <LocationDetails />
              </Section>
            </Render>
            <Divider />
            <Section style={{ paddingTop: '8px' }}>
              <TimePicker activeConveyance={conveyanceMode.type} onClose={closePopover} />
            </Section>
          </Content>
        </Container>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const StyledCloseButton = styled(Dialog.Close)(({ theme }) => ({
  position: 'absolute',
  top: -5,
  color: 'black',
  zIndex: 9999,
  cursor: 'pointer',
  borderRadius: '4px',
  padding: '4px',
  background: 'rgba(255, 255, 255, 0.5)',

  ':hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },

  ':focus-visible': {
    outline: '2px solid',
    outlineColor: theme.buttons.primary_button_background_color ?? 'inherit',
    outlineOffset: '2px',
  },
}));

const StyledButton = styled.button<{
  $active: boolean;
  $removeRightBorder?: boolean;
  $removeLeftBorder?: boolean;
}>(({ theme, $active, $removeRightBorder, $removeLeftBorder }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  alignItems: 'center',
  backgroundColor: $active ? theme.buttons.primary_button_background_color : 'transparent',
  border: '1px solid',
  borderColor: theme.header.border_color ?? '#ddd',
  borderRadius: (() => {
    if ($removeLeftBorder && !$removeRightBorder) return '0 8px 8px 0';
    if (!$removeLeftBorder && $removeRightBorder) return '8px 0 0 8px';
    if ($removeLeftBorder && $removeRightBorder) return '0';
    return '8px';
  })(),
  borderLeft: $removeLeftBorder ? 'none' : `1px solid ${theme.header.border_color ?? '#ddd'}`,
  borderRight: $removeRightBorder ? 'none' : `1px solid ${theme.header.border_color ?? '#ddd'}`,
  cursor: 'pointer',
  display: 'flex',
  gap: '4px',
  height: '32px',
  padding: '8px',
  ...getFontStyles(
    theme.buttons.primary_font,
    ['color', 'font_family', 'font_weight', 'line_height'],
    {
      color: (value) => {
        if ($active) {
          if (value === 'revert' || value === null) {
            return safePolished(readableColor)(theme.buttons.primary_button_background_color);
          }
          return value;
        }
        return theme.modals.font.color;
      },
    },
  ),
  '@media (max-width: 375px)': {
    height: '26px',
    fontSize: '12px',
  },
  '&:disabled': {
    cursor: 'not-allowed',
  },
}));

const ButtonsWrapper = styled.div<{ $isLoading: boolean }>(({ $isLoading }) => ({
  position: 'relative',
  display: 'flex',
  opacity: $isLoading ? 0.6 : 1,
  pointerEvents: $isLoading ? 'none' : 'auto',
  transition: 'opacity 0.2s linear',
}));

const SpinnerOverlay = styled.div({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

const FulfillmentTime = styled.span({
  whiteSpace: 'nowrap',
  paddingLeft: '6px',
  '@media (max-width: 375px)': {
    fontSize: '12px',
  },
});

const Trigger = styled(Dialog.Trigger)({
  all: 'unset',

  ':focus-visible': {
    outline: 'revert',
  },
});

const Container = styled.div({
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
});

interface OverlayStyles {
  $shouldDisplay: boolean;
}

const Overlay = styled(Dialog.Overlay)<OverlayStyles>(({ $shouldDisplay }) => ({
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(26, 26, 26, 0.5)',
  opacity: $shouldDisplay ? 1 : 0,
  transition: 'opacity 0.3s ease',
}));

interface ContentStyles {
  $shouldDisplay: boolean;
}

const Content = styled(Dialog.Content)<ContentStyles>(({ theme, $shouldDisplay }) => ({
  position: 'fixed',
  inset: 0,
  background: theme.modals.background_color ?? 'white',
  display: 'flex',
  flexDirection: 'column',
  transform: $shouldDisplay ? 'translateY(0)' : 'translateY(100%)',
  transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
  zIndex: 101,

  '@media (min-width: 640px)': {
    position: 'relative',
    inset: 'auto',
    width: '32rem',
    maxHeight: '80vh',
    borderRadius: '.5rem',
    boxShadow:
      '0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)',
    transform: $shouldDisplay ? 'scale(1)' : 'scale(0.95)',
    opacity: $shouldDisplay ? 1 : 0,
    transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease',
  },

  ':focus': {
    outline: 'none',
  },

  ':focus-visible': {
    outline: '2px solid',
    outlineColor: theme.buttons.primary_button_background_color ?? 'inherit',
    outlineOffset: '2px',
  },
}));

const Section = styled.div({
  padding: '1.5rem',
});

const DetailsSection = styled.div({
  padding: '1.5rem',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const Divider = styled.hr(({ theme }) => ({
  background: theme.global.primary_border_color ?? '#e5e5e5',
  border: 'none',
  height: '1px',
  margin: 0,
}));

const SwitchedToPickupNote = styled.span(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  padding: '8px',
  fontSize: '13px',
  lineHeight: '18px',
  borderRadius: '8px',
  border: `1px solid ${theme.global.primary_border_color}`,
  backgroundColor: transparentize(0.8, theme.global.primary_border_color),
  ...getFontStyles(theme.modals.font, ['color']),
  marginTop: '10px',
  marginBottom: '16px',
}));
